import { DateTime } from 'luxon';

import { FiniteTimeUnits, TgDuration } from '@playq/services-shared';
import { AccessLevel, UserApiKeyDataSerialized } from '@playq/octopus2-auth';

import { ExpirationLabels, ISelectAccessLevelOptions } from './types';

export const selectAccessLevelOptions: Readonly<ISelectAccessLevelOptions> = {
  None: AccessLevel.None,
  Read: AccessLevel.Read,
  Write: AccessLevel.Write,
  'Write Live': AccessLevel.WriteLive,
};

export const expirationPresets = [
  { label: ExpirationLabels.days7, days: 7 },
  { label: ExpirationLabels.days30, days: 30 },
  { label: ExpirationLabels.days60, days: 60 },
  { label: ExpirationLabels.days90, days: 90 },
  { label: ExpirationLabels.months6, days: 180 },
  { label: ExpirationLabels.year1, days: 365 },
  { label: ExpirationLabels.noExpiration, days: undefined },
  { label: ExpirationLabels.custom, days: 0 },
] as const;

export const labelFormatDivider = ' ('; // including space

export const formatExpirationLabel = ({ label, days }: { label: ExpirationLabels; days: number | undefined }) => {
  if (days === undefined) return 'No expiration';
  if (days === 0) return 'Custom';
  const date = DateTime.now().plus({ days });
  return `${label}${labelFormatDivider}${date.toFormat('MMM d, yyyy')})`;
};

export const expirationOptions: readonly { value: string; label: string }[] = expirationPresets.map((conf) => ({
  value: conf.label,
  label: formatExpirationLabel({ label: conf.label, days: conf.days }),
}));

export const makeTgDuration = (days: number): TgDuration => {
  const d = new TgDuration();
  d.length = days;
  d.unit = FiniteTimeUnits.DAYS;
  return d;
};

export const getInitialExpirationState = (data?: UserApiKeyDataSerialized) => {
  if (!data?.ttl) {
    return {
      mode: ExpirationLabels.noExpiration,
      duration: undefined,
    };
  }

  const days = data.ttl.length;
  const matchingPreset = expirationPresets.find((p) => p.days === days);

  if (matchingPreset) {
    return {
      mode: matchingPreset.label,
      duration: new TgDuration(data.ttl),
    };
  }

  return {
    mode: ExpirationLabels.custom,
    duration: new TgDuration(data.ttl),
    customDate: data.expiration ? DateTime.fromISO(data.expiration) : DateTime.now().plus({ days }),
  };
};
